
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Menu, User, Sun, Home, List, Info, Plus, Heart } from 'lucide-react';
import useAuth from '@/hooks/useAuth';

const MobileNavigation = () => {
  const { user, profile, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = React.useState(false);
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsOpen(false);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  const menuItems = [
    { icon: Home, label: 'Home', path: '/' },
    { icon: List, label: 'All Terraces', path: '/list' },
    { icon: Info, label: 'About', path: '/about' },
    { icon: Plus, label: 'Submit Terrace', path: '/submit-terrace' },
  ];

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="md:hidden h-10 w-10 p-0 hover:bg-peach-orange/10"
          aria-label="Open navigation menu"
        >
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent 
        side="right" 
        className="w-[300px] bg-warm-cream border-l border-peach-orange/20 p-0"
      >
        <div className="flex flex-col h-full">
          <SheetHeader className="p-6 pb-4 border-b border-peach-orange/10">
            <SheetTitle className="flex items-center gap-2 text-sunset-dark text-left">
              <Sun className="w-5 h-5 text-peach-orange" />
              FollowTheSun
            </SheetTitle>
          </SheetHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            <nav className="space-y-1 px-3">
              {menuItems.map((item) => (
                <Button
                  key={item.path}
                  variant="ghost"
                  className="w-full justify-start text-sunset-medium hover:bg-peach-orange/10 hover:text-sunset-dark h-12 px-3"
                  onClick={() => handleNavigation(item.path)}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  <span className="font-medium">{item.label}</span>
                </Button>
              ))}
            </nav>
          </div>
          
          <div className="border-t border-peach-orange/10 p-4 space-y-3">
            {user ? (
              <>
                <Button 
                  variant="default" 
                  className="w-full justify-start h-12 bg-gradient-to-r from-peach-orange to-coral-pink hover:from-peach-orange-dark hover:to-coral-pink text-white font-medium"
                  onClick={() => handleNavigation('/dashboard')}
                >
                  <User className="h-5 w-5 mr-3" />
                  {profile?.name || 'Dashboard'}
                </Button>
                
                {isAdmin && (
                  <Button 
                    variant="outline" 
                    className="w-full justify-start h-12 border-peach-orange text-peach-orange hover:bg-peach-orange hover:text-white font-medium"
                    onClick={() => handleNavigation('/admin/dashboard')}
                  >
                    Admin Dashboard
                  </Button>
                )}
                
                <Button 
                  variant="ghost" 
                  className="w-full justify-start h-12 text-coral-pink hover:bg-coral-pink/10 hover:text-coral-pink font-medium"
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start h-12 text-sunset-medium hover:bg-peach-orange/10 hover:text-sunset-dark font-medium"
                  onClick={() => handleNavigation('/login')}
                >
                  Sign In
                </Button>
                <Button 
                  variant="default" 
                  className="w-full justify-start h-12 bg-gradient-to-r from-peach-orange to-coral-pink hover:from-peach-orange-dark hover:to-coral-pink text-white font-medium"
                  onClick={() => handleNavigation('/waitlist')}
                >
                  <Heart className="h-5 w-5 mr-3" />
                  Join Waitlist
                </Button>
              </>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileNavigation;
